/*
 * Description:
 * ---------------------
 * The Firewall screen's Intrusions: the box's intrusion prevention, after
 * Fail2ban (cloud-anchor/pkg/jail, /firewall/jails). A sentence first, what
 * is banned and what is watched; then the bans in force, each with what it
 * did, how risky it looked and why, Let back in and Ada to explain it; the
 * jails, built-in and the operator's own over any log, each with its rule as
 * a sentence and an editor that starts from a ready-made filter, tries it on
 * the log's last lines, or has Ada write it; repeat offenders; what the
 * fleet shares (other sites' bans, Weavian's AI, crowdsourced lists); and the
 * addresses counted now. LetMeIn is the banner a person sees when their own
 * address is banned here, which lets them back in by a code emailed to them
 * (server/pkg/fleetbans). Wording in intrusionWords.js.
 *
 * ! file components/FirewallIntrusions.js
 */
import React, { useCallback, useState } from "react";
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiPlus } from "react-icons/fi";
import { TbNetwork, TbShieldLock, TbWorld } from "react-icons/tb";
import { FEATURE_ABSENT, FEATURE_PRESENT } from "../hooks/useEdgeFeature";
import useEdgeWrite from "../hooks/useEdgeWrite";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { IS_SINGLE_EDGE, nodeApiUrlFor, serverApiUrl } from "../serverApi";
import { askAda } from "../utils/askAda";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { AiIcon } from "./AiIcon";
import { useConfirm } from "./ConfirmModal";
import { count } from "./firewallWords";
import * as W from "./intrusionWords";

const WINDOWS = [60, 300, 600, 1800, 3600, 21600, 86400];
const LENGTHS = [60, 600, 3600, 21600, 86400, 604800, 2592000];

/* useIntrusions reads the server's view of the caller's sites: the caller's
   own address and where it is banned, and the fleet's list in counts. Only
   the portal has it. */
export function useIntrusions(selectedNode) {
  const [view, setView] = useState(null);
  const read = useCallback(async () => {
    const res = await fetchWithTimeout(serverApiUrl("/firewall/intrusions"), { credentials: "same-origin" }).catch(() => null);
    if (res?.ok) setView(await res.json().catch(() => null));
  }, []);
  useVisibleInterval(read, 30000, !IS_SINGLE_EDGE && !!selectedNode);
  const banned = (view?.you?.banned || []).filter((b) => b.node === selectedNode);
  return { view, banned, refresh: read };
}

async function post(path, body) {
  const res = await fetchWithTimeout(serverApiUrl(path), {
    method: "POST", credentials: "same-origin",
    headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).catch(() => null);
  const out = res ? await res.json().catch(() => ({})) : {};
  if (!res?.ok) throw new Error(out.error || "The Weavian server did not answer. Try again in a minute.");
  return out;
}

/* LetMeIn: the caller's own address is banned at this site. */
export function LetMeIn({ selectedNode, intrusions }) {
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const b = intrusions.banned[0];
  if (done) {
    return <div className="fw-notice is-ok" role="status"><TbShieldLock size={16} /> <span>{W.YOU.done(done)}</span></div>;
  }
  if (!b) return null;
  const you = intrusions.view.you;
  const run = async (f) => {
    setBusy(true);
    setError("");
    try {
      await f();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const ask = () => run(async () => setSent(await post("/firewall/letmein", { node: selectedNode })));
  const verify = (e) => {
    e.preventDefault();
    run(async () => {
      const out = await post("/firewall/letmein/verify", { node: selectedNode, code: code.trim() });
      setDone(out.address);
      intrusions.refresh();
    });
  };
  return (
    <div className="fw-notice is-bad fw-letmein" role="alert">
      <FiAlertTriangle size={16} />
      <div>
        <p>{W.YOU.banned(you.address, b)}</p>
        {b.why_not ? <p>{b.why_not}</p> : !you.can_ask ? <p>{W.YOU.noEmail}</p> : !sent ? (
          <p>{W.YOU.canAsk()} <button type="button" className="fw-btn" disabled={busy} onClick={ask}>{W.YOU.ask}</button></p>
        ) : (
          <form className="fw-add" onSubmit={verify}>
            <span>{W.YOU.sent(sent.to)}</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code"
              placeholder="123456" aria-label={W.YOU.code} />
            <button type="submit" className="fw-btn is-primary" disabled={busy || code.trim().length !== 6}>{W.YOU.verify}</button>
          </form>
        )}
        {error && <p className="fw-field-bad">{error}</p>}
      </div>
    </div>
  );
}

/* One ban's row, with its reasons and Let back in under it when opened. */
function BanRow({ b, selectedNode, locked, send }) {
  const [open, setOpen] = useState(false);
  const [lifting, setLifting] = useState(false);
  const [trust, setTrust] = useState(false);
  const risk = W.riskWords(b);
  return (
    <>
      <tr>
        <td>
          <button type="button" className="fw-ip-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />} <code>{b.address}</code>
          </button>
        </td>
        <td><strong>{W.jailName(b.jail)}</strong> <small>{W.didWords(b)}</small></td>
        <td>
          <span className={`fw-why is-${risk.tone}`}>{risk.label}</span>
          {!b.manual && <span className="fw-ip-score" title={`Risk score ${b.score} of 100`}><i style={{ width: `${b.score}%` }} /></span>}
        </td>
        <td className="fw-log-when">{W.untilWords(b)}</td>
        <td>
          <div className="fw-ip-acts">
            <button type="button" className="fw-btn is-quiet" disabled={locked} onClick={() => setLifting(!lifting)}>{W.BANS.unban}</button>
            <button type="button" className="fw-icon-btn is-ai" aria-label={W.BANS.ask} title={W.BANS.ask}
              onClick={() => askAda(selectedNode, W.BANS.question(b), { send: true })}>
              <AiIcon size={16} />
            </button>
          </div>
        </td>
      </tr>
      {(open || lifting) && (
        <tr className="fw-ip-more">
          <td colSpan={5}>
            {open && (
              <>
                <strong>{W.BANS.why}</strong>
                <ul>{(b.reasons || []).map((r) => <li key={r}>{r}</li>)}</ul>
              </>
            )}
            {lifting && (
              <div className="fw-add">
                <span>{W.BANS.unbanAsk(b).message}</span>
                <label className="fw-check"><input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} /> {W.BANS.trust}</label>
                <button type="button" className="fw-btn is-primary" disabled={locked}
                  onClick={() => send("POST", "/firewall/jails/unban", { address: b.address, trust_s: trust ? 86400 : 0 })}>
                  {W.BANS.unbanAsk(b).confirmLabel}
                </button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function ManualBan({ locked, send, onDone }) {
  const [address, setAddress] = useState("");
  const [seconds, setSeconds] = useState(86400);
  const [reason, setReason] = useState("");
  const M = W.MANUAL;
  const save = async (e) => {
    e.preventDefault();
    if (await send("POST", "/firewall/jails/ban", { address: address.trim(), seconds: Number(seconds), reason: reason.trim() })) onDone();
  };
  return (
    <form className="fw-ip-form" onSubmit={save}>
      <label>{M.address}<input className="fw-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={M.placeholder} /></label>
      <label>{M.howLong}
        <select className="fw-input" value={seconds} onChange={(e) => setSeconds(e.target.value)}>
          {M.lengths.map(([s, label]) => <option key={s} value={s}>{label}</option>)}
        </select>
      </label>
      <label className="is-wide">{M.reason}<input className="fw-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={M.reasonPlaceholder} /></label>
      <div className="fw-ip-form-acts">
        <button type="button" className="fw-btn is-quiet" onClick={onDone}>{M.cancel}</button>
        <button type="submit" className="fw-btn is-primary" disabled={locked || !address.trim() || !reason.trim()}>{M.save}</button>
      </div>
    </form>
  );
}

function Bans({ st, selectedNode, locked, send }) {
  const [adding, setAdding] = useState(false);
  const bans = st.bans || [];
  return (
    <section className="fw-card">
      <header className="fw-card-head is-split">
        <div>
          <h3 className="fw-card-title">{W.BANS.title}</h3>
          <p>{W.INTRO}</p>
        </div>
        <button type="button" className="fw-btn" disabled={locked} onClick={() => setAdding(!adding)}><FiPlus size={14} /> {W.BANS.add}</button>
      </header>
      {adding && <ManualBan locked={locked} send={send} onDone={() => setAdding(false)} />}
      {bans.length === 0 ? <p className="fw-muted">{W.BANS.none}</p> : (
        <div className="fw-log-wrap">
          <table className="fw-log fw-ip-bans">
            <thead>
              <tr>{Object.values(W.BANS.cols).map((c, i) => <th key={i}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {bans.map((b) => <BanRow key={`${b.address}-${b.at}`} b={b} selectedNode={selectedNode} locked={locked} send={send} />)}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* The fields of a jail as the editor holds them: patterns one a line. */
const draftOf = (j) => ({
  name: j?.name || "", about: j?.about || "", builtin: !!j?.builtin, enabled: j ? !!j.enabled : true,
  max_retry: j?.max_retry || 5, find_s: j?.find_s || 600, ban_s: j?.ban_s || 600,
  preset: j?.preset || "", decoy: !!j?.decoy,
  kind: j?.source?.file ? "file" : j?.source?.identifier ? "identifier" : "unit",
  where: j?.source?.file || j?.source?.identifier || j?.source?.unit || "",
  fail: (j?.filter?.fail || []).join("\n"), ignore: (j?.filter?.ignore || []).join("\n"),
});

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function JailEditor({ jail, presets, selectedNode, locked, send, onDone }) {
  const [d, setD] = useState(draftOf(jail));
  const [tried, setTried] = useState(null);
  const [trying, setTrying] = useState(false);
  const [tryError, setTryError] = useState("");
  const set = (k) => (e) => setD({ ...d, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const E = W.EDITOR;
  const source = { [d.kind]: d.where.trim() };
  const applyPreset = (name) => {
    const p = presets.find((x) => x.name === name);
    if (!p) {
      setD({ ...d, preset: "" });
      return;
    }
    setD({
      ...d, preset: p.name, name: d.name || p.name, about: d.about || p.about,
      kind: p.file ? "file" : "unit", where: p.file || p.unit || d.where,
      fail: p.fail.join("\n"), ignore: (p.ignore || []).join("\n"),
    });
  };
  const tryIt = async () => {
    setTrying(true);
    setTryError("");
    const q = new URLSearchParams({ [d.kind]: d.where.trim(), lines: "200" });
    lines(d.fail).forEach((f) => q.append("fail", f));
    lines(d.ignore).forEach((f) => q.append("ignore", f));
    const res = await fetchWithTimeout(nodeApiUrlFor(selectedNode)(`/firewall/jails/test?${q}`), {}, 30000).catch(() => null);
    setTrying(false);
    if (!res?.ok) {
      setTryError(res ? (await res.text()).trim() : "The box did not answer.");
      return;
    }
    setTried(await res.json());
  };
  const save = async (e) => {
    e.preventDefault();
    const body = d.builtin
      ? { name: d.name, enabled: d.enabled, max_retry: Number(d.max_retry), find_s: Number(d.find_s), ban_s: Number(d.ban_s) }
      : {
        name: d.name.trim(), about: d.about.trim(), enabled: d.enabled, max_retry: Number(d.max_retry), find_s: Number(d.find_s),
        ban_s: Number(d.ban_s), preset: d.preset, source, filter: { fail: lines(d.fail), ignore: lines(d.ignore) }, decoy: d.decoy,
      };
    if (await send("PUT", "/firewall/jails", body)) onDone();
  };
  const triedLines = tried?.lines || [];
  const counted = triedLines.filter((l) => l.counts);
  return (
    <form className="fw-ip-editor" onSubmit={save}>
      <h4>{jail ? E.titleEdit(jail) : E.titleNew}</h4>
      {!d.builtin && (
        <>
          <p className="fw-muted">{E.intro}</p>
          <div className="fw-ip-form">
            <label>{E.preset}
              <select className="fw-input" value={d.preset} onChange={(e) => applyPreset(e.target.value)}>
                <option value="">{E.presetNone}</option>
                {presets.map((p) => <option key={p.name} value={p.name}>{p.service}: {p.about}</option>)}
              </select>
            </label>
            <label>{E.name}<input className="fw-input" value={d.name} onChange={set("name")} placeholder={E.namePlaceholder} disabled={!!jail} /></label>
            <label className="is-wide">{E.about}<input className="fw-input" value={d.about} onChange={set("about")} placeholder={E.aboutPlaceholder} /></label>
            <label>{E.where}
              <select className="fw-input" value={d.kind} onChange={set("kind")}>
                {E.whereKinds.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </label>
            <label><span aria-hidden="true">&nbsp;</span>
              <input className="fw-input" value={d.where} onChange={set("where")} placeholder={E.wherePlaceholders[d.kind]} aria-label={E.where} />
            </label>
            <label className="is-wide">{E.fail}
              <textarea className="fw-input fw-ip-patterns" value={d.fail} onChange={set("fail")} rows={4} spellCheck={false} />
              <small>{E.failHelp}</small>
            </label>
            <label className="is-wide">{E.ignore}
              <textarea className="fw-input fw-ip-patterns" value={d.ignore} onChange={set("ignore")} rows={2} spellCheck={false} />
              <small>{E.ignoreHelp}</small>
            </label>
          </div>
          <label className="fw-check">
            <input type="checkbox" checked={d.decoy} onChange={set("decoy")} />
            <span>{E.decoy}<small>{E.decoyHelp}</small></span>
          </label>
          <div className="fw-ip-try">
            <button type="button" className="fw-btn" disabled={trying || !d.where.trim() || !lines(d.fail).length} onClick={tryIt}>
              {trying ? E.testing : E.test}
            </button>
            <button type="button" className="fw-btn is-ai" onClick={() => askAda(selectedNode, E.adaQuestion(W.sourceWords(source), d.about), { send: true })}>
              <AiIcon size={15} /> {E.ada}
            </button>
            {tryError && <span className="fw-field-bad">{tryError}</span>}
          </div>
          {tried && (
            <div className="fw-ip-tried">
              <p>{counted.length ? E.tested(counted.length, triedLines.length) : E.testedNone(triedLines.length)}</p>
              {counted.length > 0 && (
                <ul>
                  {counted.slice(-8).map((l, i) => (
                    <li key={i}><code>{l.line}</code> <span className="fw-why is-look">{l.host}{l.user ? `, ${l.user}` : ""}</span></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
      <p className="fw-ip-sentence">{E.thresholds({ max_retry: d.max_retry, find_s: d.find_s, ban_s: d.ban_s })}</p>
      <div className="fw-ip-form">
        <label>{E.maxRetry}<input className="fw-input" type="number" min={1} max={1000} value={d.max_retry} onChange={set("max_retry")} /></label>
        <label>{E.findS}
          <select className="fw-input" value={d.find_s} onChange={set("find_s")}>
            {[...new Set([...WINDOWS, Number(d.find_s)])].sort((a, b) => a - b).map((s) => <option key={s} value={s}>{W.span(s)}</option>)}
          </select>
        </label>
        <label>{E.banS}
          <select className="fw-input" value={d.ban_s} onChange={set("ban_s")}>
            {[...new Set([...LENGTHS, Number(d.ban_s)])].sort((a, b) => a - b).map((s) => <option key={s} value={s}>{W.span(s)}</option>)}
          </select>
        </label>
      </div>
      <div className="fw-ip-form-acts">
        <button type="button" className="fw-btn is-quiet" onClick={onDone}>{E.cancel}</button>
        <button type="submit" className="fw-btn is-primary" disabled={locked || !d.name.trim() || (!d.builtin && (!d.where.trim() || !lines(d.fail).length))}>{E.save}</button>
      </div>
    </form>
  );
}

function Jails({ st, selectedNode, locked, send }) {
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();
  const J = W.JAILS_CARD;
  const toggle = (j) => send("PUT", "/firewall/jails", { name: j.name, enabled: !j.enabled });
  const remove = async (j) => {
    if (await confirm(J.removeAsk(j))) send("DELETE", `/firewall/jails?name=${encodeURIComponent(j.name)}`);
  };
  return (
    <section className="fw-card">
      <header className="fw-card-head is-split">
        <div>
          <h3 className="fw-card-title">{J.title}</h3>
          <p>{J.means}</p>
        </div>
        <button type="button" className="fw-btn" disabled={locked} onClick={() => setEditing("new")}><FiPlus size={14} /> {J.add}</button>
      </header>
      {editing === "new" && <JailEditor presets={st.presets || []} selectedNode={selectedNode} locked={locked} send={send} onDone={() => setEditing(null)} />}
      <ul className="fw-protects">
        {(st.jails || []).map((j) => (
          <li key={j.name} className={`fw-protect is-${j.enabled ? "on" : "off"}`}>
            <button type="button" role="switch" aria-checked={!!j.enabled} aria-label={W.jailName(j)} className="fw-ip-switch"
              disabled={locked} onClick={() => toggle(j)}><span /></button>
            <div className="fw-protect-main">
              <strong>{W.jailName(j)}</strong>
              <p>{W.capital(W.jailCatches(j))}{!j.builtin && j.source ? `, in ${W.sourceWords(j.source)}` : ""}.</p>
              <p className="fw-protect-live">{W.ruleWords(j)}</p>
              {editing === j.name && <JailEditor jail={j} presets={st.presets || []} selectedNode={selectedNode} locked={locked} send={send} onDone={() => setEditing(null)} />}
            </div>
            <div className="fw-protect-now">
              <strong>{J.banned(j.banned) || (j.enabled ? "Watching" : "Off")}</strong>
              {J.failing(j.failing) && <span>{J.failing(j.failing)}</span>}
              <button type="button" className="fw-link" disabled={locked} onClick={() => setEditing(editing === j.name ? null : j.name)}>{J.edit}</button>
              {!j.builtin && <button type="button" className="fw-link is-bad" disabled={locked} onClick={() => remove(j)}>{J.remove}</button>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Recidive({ st, locked, send }) {
  const r = st.recidive || { enabled: true, bans: 3 };
  const R = W.RECIDIVE;
  const put = (change) => send("PUT", "/firewall/jails/recidive", { ...r, ...change });
  return (
    <section className="fw-card">
      <header className="fw-card-head is-split">
        <div>
          <h3 className="fw-card-title">{R.title}</h3>
          <p>{r.enabled ? R.sentence(r) : R.off}</p>
        </div>
        <button type="button" role="switch" aria-checked={!!r.enabled} aria-label={R.title} className="fw-ip-switch"
          disabled={locked} onClick={() => put({ enabled: !r.enabled })}><span /></button>
      </header>
      {r.enabled && (
        <div className="fw-ip-inline">
          <label>{R.bans}
            <select className="fw-input" value={r.bans} disabled={locked} onChange={(e) => put({ bans: Number(e.target.value) })}>
              {[2, 3, 4, 5, 6, 8, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="fw-check"><input type="checkbox" checked={!!r.permanent} disabled={locked} onChange={(e) => put({ permanent: e.target.checked })} /> {R.permanent}</label>
        </div>
      )}
    </section>
  );
}

function Fleet({ st }) {
  const F = W.FLEET;
  const fleet = st.fleet?.fleet || {};
  const feeds = st.fleet?.feeds || {};
  const d = st.dropped || {};
  const feedEntries = feeds.entries || 0;
  const rows = [
    { key: "fleet", Icon: TbNetwork, n: fleet.sources?.fleet || 0, stopped: d.fleet },
    { key: "model", Icon: AiIcon, n: fleet.sources?.model || 0, ai: true },
    { key: "feeds", Icon: TbWorld, n: feedEntries, stopped: d.feeds, none: !feedEntries && F.noFeeds },
  ];
  return (
    <section className="fw-card">
      <header className="fw-card-head">
        <h3 className="fw-card-title">{F.title}</h3>
        <p>{F.means}</p>
      </header>
      <ul className="fw-protects">
        {rows.map((r) => (
          <li key={r.key} className={`fw-protect is-${r.ai ? "ai" : r.n ? "on" : "off"}`}>
            <span className="fw-protect-icon" aria-hidden="true"><r.Icon size={20} strokeWidth={1.8} /></span>
            <div className="fw-protect-main">
              <strong>{F.rows[r.key].what}</strong>
              <p>{F.rows[r.key].why}</p>
              {r.none && <p className="fw-protect-live">{r.none}</p>}
            </div>
            <div className="fw-protect-now">
              <strong>{F.entries(r.n)}</strong>
              {r.stopped !== undefined && <span>{F.stopped(r.stopped || 0)}</span>}
            </div>
          </li>
        ))}
      </ul>
      <p className="fw-muted">{F.got(fleet.got)} {F.never}</p>
    </section>
  );
}

function Failing({ st }) {
  const f = st.failing || [];
  if (!f.length) return null;
  const F = W.FAILING;
  return (
    <details className="fw-card fw-ip-failing">
      <summary><strong>{F.title(f.length)}</strong> <span>{F.means}</span></summary>
      <div className="fw-log-wrap">
        <table className="fw-log cards-on-phone">
          <thead><tr>{Object.values(F.cols).map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>
            {f.map((x) => (
              <tr key={x.address}>
                <td><code>{x.address}</code></td>
                <td data-label={F.cols.week}>{count((x.days || []).reduce((a, dc) => a + dc.n, 0))}{x.users > 1 ? <small> {x.users} user names</small> : null}</td>
                <td data-label={F.cols.days}>{(x.days || []).length}</td>
                <td data-label={F.cols.jails}>{(x.jails || []).map((j) => W.jailName(j)).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

export default function FirewallIntrusions({ selectedNode, jails, locked }) {
  const { busy, error, send } = useEdgeWrite(selectedNode, jails.refresh);
  const st = jails.state === FEATURE_PRESENT ? jails.payload : null;
  if (jails.state === FEATURE_ABSENT) return <div className="fw-card"><p className="fw-muted">{W.SECTION.unavailable}</p></div>;
  if (!st) return <div className="fw-card"><p className="fw-muted">{W.SECTION.unreachable}</p></div>;
  if (!st.available) return <div className="fw-card"><p className="fw-muted">{st.reason || W.SECTION.unavailable}</p></div>;
  const off = locked || busy;
  return (
    <div className="fw-intrusions">
      <section className="fw-card">
        <header className="fw-card-head">
          <h3 className="fw-card-title"><TbShieldLock size={18} aria-hidden="true" /> {W.SECTION.title}</h3>
          <p className="fw-lead">{W.lead(st)}</p>
        </header>
        {st.problem && <p className="fw-inline-note is-warn">{st.problem}</p>}
        {st.unreadable && <p className="fw-inline-note is-warn">{st.unreadable}</p>}
      </section>
      {error && <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{error}</span></div>}
      <Bans st={st} selectedNode={selectedNode} locked={off} send={send} />
      <Jails st={st} selectedNode={selectedNode} locked={off} send={send} />
      <Recidive st={st} locked={off} send={send} />
      <Fleet st={st} />
      <Failing st={st} />
    </div>
  );
}
